<script setup>
import axios from 'axios';
import { onMounted, ref } from 'vue';
import { RouterLink } from 'vue-router';
import XlButton from '../xl/XlButton.vue';
import XlTable from '../xl/XlTable.vue';
import XlTag from '../xl/XlTag.vue';
import { money } from '../../helpers/coverage';
import { plural } from '../../helpers/plural';
import { longDate } from '../../helpers/usDate';

const props = defineProps({
  lineId: { type: Number, required: true },
  scoped: { type: Boolean, default: false },
});

const emit = defineEmits(['count']);

const EMPTY = {
  owner: 'No registered policyholders hold this line yet. Anyone imported or added to it appears here once they finish registering.',
  broker: 'None of your registered policyholders hold this line yet. People in your book appear here once they finish registering.',
};

const policies = ref([]);
const error = ref('');
const loading = ref(false);
const loaded = ref(false);

const endpoint = () => `${window.store?.event_backend_api ?? '/api'}/policy-line-tags/${props.lineId}/policies`;
const failure = (e) => e.response?.data?.message ?? "Couldn't load the policies on this line.";

const load = async () => {
  if (loading.value) return;

  loading.value = true;

  try {
    const { data } = await axios.get(endpoint());
    policies.value = data?.data?.policies ?? [];
    error.value = '';
    loaded.value = true;
    emit('count', policies.value.length);
  } catch (e) {
    error.value = failure(e);
  } finally {
    loading.value = false;
  }
};

onMounted(load);
</script>

<template>
  <div class="line-policies">
    <div v-if="error" class="line-policies__error" data-testid="line-policies-error" role="alert">
      <span>{{ error }}</span>
      <XlButton variant="ghost" size="sm" :disabled="loading" data-testid="line-policies-retry" @click="load">Retry</XlButton>
    </div>

    <p v-else-if="!loaded" class="line-policies__note">Loading policies…</p>

    <p v-else-if="!policies.length" class="line-policies__note" data-testid="line-policies-empty">{{ props.scoped ? EMPTY.broker : EMPTY.owner }}</p>

    <XlTable v-else>
      <template #head>
        <th>Policyholder</th>
        <th>Policy number</th>
        <th>Monthly premium</th>
        <th>Renewal date</th>
      </template>
      <tr v-for="policy in policies" :key="policy.holder.user_id">
        <td>
          <RouterLink :to="`/policyholders/${policy.holder.user_id}`">{{ policy.holder.name || 'Policyholder' }}</RouterLink>
          <XlTag v-if="policy.policy_count > 1" class="line-policies__tag">{{ plural(policy.policy_count, 'policy', 'policies') }}</XlTag>
        </td>
        <td>{{ policy.policy_number || '—' }}</td>
        <td>{{ money(policy.monthly_premium, policy.currency) || '—' }}</td>
        <td>{{ policy.id ? longDate(policy.renewal_date) || 'No date' : '—' }}</td>
      </tr>
    </XlTable>
  </div>
</template>

<style scoped>
.line-policies {
  padding: 4px 18px 16px;
  border-top: 1px solid var(--xl-color-border-subtle, var(--xl-color-border));
  font-family: var(--xl-font-family);
}

.line-policies__note { margin: 14px 0 0; color: var(--xl-color-outline); }

.line-policies__error {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 14px;
  color: var(--xl-color-error);
}

.line-policies__tag { margin-left: 8px; }
</style>
